// RODÍZIO MUNICIPAL DE SÃO PAULO — a parte que só depende da PLACA.
//
// Módulo PURO (nenhum import de runtime), pelo mesmo motivo do `trackFilter`: dá para exercitar com
// entradas sintéticas, e errar aqui muda a rota da semana em silêncio.
//
// A regra da CET: a restrição é por ÚLTIMO DÍGITO da placa e vale um dia útil por placa —
// segunda 1-2, terça 3-4, quarta 5-6, quinta 7-8, sexta 9-0. Fim de semana não tem rodízio.
//
// ⚠️ ESCOPO DESTE ARQUIVO: só o "QUANDO". O "ONDE" (Centro Expandido) mora em outro lugar, porque
// depende de geometria e de coordenada do cliente. Separados de propósito: o Meu Perfil precisa só
// do dia (para o vendedor conferir que digitou a placa certa) e não deve arrastar polígono junto.

/** Dias da semana no mesmo índice que `Date.getDay()`: 0 = domingo … 6 = sábado. */
export const SEG = 1, TER = 2, QUA = 3, QUI = 4, SEX = 5;

const NOME_DIA: Record<number, string> = {
  1: 'segunda-feira', 2: 'terça-feira', 3: 'quarta-feira', 4: 'quinta-feira', 5: 'sexta-feira',
};

/** Último DÍGITO da placa, ou null se não houver nenhum.
 *
 *  Serve aos dois formatos sem tratamento especial: a antiga `ABC-1234` termina em dígito e a
 *  Mercosul `ABC1D23` também (o padrão é LLLNLNN). Por isso basta pegar o último dígito da string,
 *  em vez de tentar reconhecer o formato — o que quebraria a cada placa fora do padrão esperado. */
export function ultimoDigitoPlaca(placa: string | null | undefined): number | null {
  const digitos = String(placa ?? '').replace(/\D/g, '');
  if (!digitos) return null;
  return Number(digitos[digitos.length - 1]);
}

/** Dia da semana restrito para a placa (1=segunda … 5=sexta), ou null se a placa não tem dígito.
 *
 *  Fórmula em vez de tabela: `ceil(d/2)` já dá 1-2→seg, 3-4→ter, 5-6→qua, 7-8→qui, 9→sex; o zero é o
 *  único caso fora da progressão (fecha a sexta junto com o 9). */
export function diaRodizioDaPlaca(placa: string | null | undefined): number | null {
  const d = ultimoDigitoPlaca(placa);
  if (d == null) return null;
  return d === 0 ? SEX : Math.ceil(d / 2);
}

/** Rótulo pronto para a tela: "quarta-feira", ou null quando não dá para determinar. */
export function nomeDiaRodizio(placa: string | null | undefined): string | null {
  const dia = diaRodizioDaPlaca(placa);
  return dia == null ? null : (NOME_DIA[dia] ?? null);
}

// ---------------------------------------------------------------------------------------------
// O "ONDE": a zona de rodízio (Centro Expandido / Mini Anel Viário)
// ---------------------------------------------------------------------------------------------
//
// ⚠️⚠️ ESTE POLÍGONO É APROXIMADO E FOI DESENHADO À MÃO. Ele segue o contorno descrito pela CET —
// Marginal Tietê ao norte, Marginal Pinheiros a oeste, Av. dos Bandeirantes ao sul, Complexo Viário
// Maria Maluf a sudeste e Salim Farah Maluf / Anhaia Mello a leste — mas os vértices são estimados,
// NÃO extraídos da fonte oficial. Antes de confiar nele para tirar visita de alguém, substitua pelo
// polígono oficial da CET (ou pelos polígonos de distrito do IBGE). É um vetor só: trocar é editar
// esta constante, nada mais.
//
// O ERRO NÃO É SIMÉTRICO, e é por isso que há uma segunda amarra:
//  • falso POSITIVO (cliente marcado como dentro sem estar) → a zona dele é remanejada à toa;
//  • falso NEGATIVO (cliente dentro e não detectado) → o vendedor entra na zona no dia restrito e
//    leva multa.
// Como o desenho é aproximado nas bordas, exigimos TAMBÉM que o município seja São Paulo (ver
// `ehZonaRodizio`). Um cliente de Guarulhos ou Osasco perto da divisa não é marcado por engano.
export const CENTRO_EXPANDIDO_SP: ReadonlyArray<{ lat: number; lng: number }> = [
  { lat: -23.5180, lng: -46.7000 }, // Marginal Tietê, trecho oeste
  { lat: -23.5150, lng: -46.6280 }, // Tietê ~ Ponte das Bandeiras
  { lat: -23.5210, lng: -46.5750 }, // Tietê ~ encontro com Salim Farah Maluf
  { lat: -23.5530, lng: -46.5700 }, // Salim Farah Maluf
  { lat: -23.5750, lng: -46.5700 }, // Anhaia Mello
  { lat: -23.6180, lng: -46.6080 }, // Complexo Viário Maria Maluf
  { lat: -23.6100, lng: -46.6400 }, // Av. dos Bandeirantes, trecho leste
  { lat: -23.6060, lng: -46.6960 }, // Bandeirantes ~ Marginal Pinheiros
  { lat: -23.5700, lng: -46.7000 }, // Marginal Pinheiros
  { lat: -23.5560, lng: -46.7100 }, // Pinheiros ~ Ponte Cidade Universitária
  { lat: -23.5230, lng: -46.7360 }, // Cebolão (Pinheiros × Tietê)
];

/** Ponto-em-polígono por ray casting (par/ímpar).
 *
 *  Em lat/lng cru, sem projetar: numa área de ~15 km a distorção não muda nenhuma decisão, e projetar
 *  traria dependência e erro próprio. O laço é O(n) com n=11 — roda por cliente sem custo relevante. */
export function dentroDoPoligono(p: { lat: number; lng: number }, poly: ReadonlyArray<{ lat: number; lng: number }>): boolean {
  let dentro = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const yi = poly[i].lat, xi = poly[i].lng;
    const yj = poly[j].lat, xj = poly[j].lng;
    // `(yi > p.lat) !== (yj > p.lat)` garante que a aresta cruza a horizontal do ponto; a segunda
    // parte testa se o cruzamento fica à DIREITA dele. Vértice exatamente na linha conta uma vez só
    // porque a comparação é estritamente `>` de um lado.
    const cruza = (yi > p.lat) !== (yj > p.lat)
      && p.lng < ((xj - xi) * (p.lat - yi)) / (yj - yi) + xi;
    if (cruza) dentro = !dentro;
  }
  return dentro;
}

// Conjunto explícito em vez de remover acento por código: `String.prototype.normalize` é NO-OP em
// algumas versões do Hermes (o repo já apanhou disso — ver `normalizarBusca` em clientsListDisk).
// Como só precisamos reconhecer UM município, listar as grafias é mais barato e mais seguro.
const SAO_PAULO = new Set(['sao paulo', 'são paulo', 'sp', 'sao paulo/sp', 'são paulo/sp']);
export function ehMunicipioSaoPaulo(municipio: string | null | undefined): boolean {
  return SAO_PAULO.has(String(municipio ?? '').trim().toLowerCase());
}

/** O cliente está na zona de rodízio? Exige as DUAS coisas — município São Paulo e dentro do
 *  polígono —, porque o polígono é aproximado nas bordas e sozinho marcaria vizinhos da divisa. */
export function ehZonaRodizio(pos: { lat: number; lng: number }, municipio: string | null | undefined): boolean {
  if (!ehMunicipioSaoPaulo(municipio)) return false;
  return dentroDoPoligono(pos, CENTRO_EXPANDIDO_SP);
}

/** Normaliza para exibição/gravação: maiúsculas, sem espaço nas pontas, sem separador.
 *
 *  Guardamos SEM o hífen para o `ultimoDigitoPlaca` e as comparações não dependerem de o vendedor ter
 *  digitado 'ABC-1D23' ou 'ABC1D23'. O teto de 10 caracteres espelha o da firestore.rules — validar
 *  só no cliente não protege nada, e validar só na regra dá erro feio na cara do vendedor. */
export function normalizarPlaca(placa: string): string {
  return String(placa || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
}
